import { Instagram } from "lucide-react";
import { ASSETS } from "@/lib/assets";
import { waLink, PRESET } from "@/lib/whatsapp";

// Artist profiles: May (first), EEME, Tyson, Alvaro
const cardBase =
  "group bg-bendito-surface border border-bendito-border hover:border-bendito-orange transition-colors duration-300";

export const Artists = ({ t }) => {
  const artists = [
    {
      key: "may",
      name: "MAY",
      photo: ASSETS.artists.may,
      ig: "https://www.instagram.com/may.tattooer/",
      igHandle: "@may.tattooer",
      data: t.artists.may,
      waMsg: PRESET.may,
      testid: "artist-may",
    },
    {
      key: "eeme",
      name: "EEME",
      photo: ASSETS.artists.eeme,
      ig: "https://www.instagram.com/eeme_tattoo/",
      igHandle: "@eeme_tattoo",
      data: t.artists.eeme,
      waMsg: PRESET.eeme,
      testid: "artist-eeme",
    },
    {
      key: "tyson",
      name: "TYSON",
      photo: ASSETS.artists.tyson,
      ig: "https://www.instagram.com/tysonpastel66/",
      igHandle: "@tysonpastel66",
      data: t.artists.tyson,
      waMsg: PRESET.tyson,
      testid: "artist-tyson",
    },
    {
      key: "alvaro",
      name: "ALVARO",
      photo: ASSETS.artists.alvaro,
      ig: "https://www.instagram.com/dziczektattoo/",
      igHandle: "@dziczektattoo",
      data: t.artists.alvaro,
      waMsg: PRESET.alvaro,
      testid: "artist-alvaro",
    },
    {
      key: "avelino",
      name: "AVELINO",
      photo: ASSETS.artists.avelino,
      ig: "https://www.instagram.com/avelinoo_tattoo",
      igHandle: "@avelinoo_tattoo",
      data: t.artists.avelino,
      waMsg: PRESET.avelino,
      testid: "artist-avelino",
    },
    {
      key: "ezzer",
      name: "EZ'ZER",
      photo: ASSETS.artists.ezzer,
      ig: "https://www.instagram.com/ezzer___",
      igHandle: "@ezzer___",
      data: t.artists.ezzer,
      waMsg: PRESET.ezzer,
      testid: "artist-ezzer",
    },
  ];

  return (
    <section
      id="artists"
      className="relative py-14 md:py-20 bg-bendito-surface"
      data-testid="artists-section"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="mb-12 reveal">
          <div className="eyebrow mb-3">{t.artists.eyebrow}</div>
          <h2 className="font-heading text-bendito-ink text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight">
            <span className="ink-underline">{t.artists.title}</span>
          </h2>
          <p className="text-bendito-muted mt-3 max-w-xl">{t.artists.sub}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {artists.map((a) => (
            <article key={a.key} className={`${cardBase} glow-on-hover touch-lift reveal flex flex-col h-full`} data-testid={a.testid}>
              <div className="relative aspect-[4/4] overflow-hidden bg-bendito-elevated">
                {a.photo ? (
                  <img
                    src={a.photo}
                    alt={a.name}
                    loading="lazy"
                    className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-700"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-center px-6">
                    <div className="font-heading text-bendito-orange text-7xl tracking-wider">
                      {a.name}
                    </div>
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-bendito-muted mt-3">
                      {t.artists.comingSoon}
                    </div>
                  </div>
                )}
                {a.comingSoon && a.photo && (
                  <div className="absolute top-3 left-3 font-mono text-[10px] tracking-[0.3em] uppercase bg-bendito-orange text-black px-2 py-1">
                    {t.artists.comingSoon}
                  </div>
                )}
              </div>

              <div className="p-6 md:p-7 flex flex-col flex-1">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-heading text-bendito-ink text-3xl tracking-wider">
                    {a.name}
                  </h3>
                  <a
                    href={a.ig}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-bendito-muted hover:text-bendito-orange transition-colors"
                    aria-label={`Instagram ${a.igHandle}`}
                    data-testid={`${a.testid}-ig`}
                  >
                    <Instagram size={18} strokeWidth={1.5} />
                  </a>
                </div>
                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-bendito-orange mt-1">
                  {a.data.title}
                </div>

                <p className="text-bendito-muted text-sm leading-relaxed mt-4">
                  {a.data.bio}
                </p>

                <dl className="mt-5 space-y-3 text-xs">
                  <div>
                    <dt className="font-mono uppercase tracking-[0.2em] text-bendito-dim text-[9px]">
                      {t.artists.specialties}
                    </dt>
                    <dd className="text-bendito-ink/90 mt-0.5">{a.data.specialties}</dd>
                  </div>
                  <div>
                    <dt className="font-mono uppercase tracking-[0.2em] text-bendito-dim text-[9px]">
                      {t.artists.languages}
                    </dt>
                    <dd className="text-bendito-ink/90 mt-0.5">{a.data.languages}</dd>
                  </div>
                  {a.data.experience && (
                    <div>
                      <dt className="font-mono uppercase tracking-[0.2em] text-bendito-dim text-[9px]">
                        {t.artists.experience}
                      </dt>
                      <dd className="text-bendito-ink/90 mt-0.5">{a.data.experience}</dd>
                    </div>
                  )}
                </dl>

                <a
                  href={waLink(a.waMsg)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-auto pt-8 inline-flex items-center justify-center w-full border border-bendito-ink hover:bg-bendito-orange hover:border-bendito-orange hover:text-black text-bendito-ink font-heading tracking-[0.22em] text-sm px-6 py-3 transition-colors"
                  data-testid={`${a.testid}-book-btn`}
                >
                  {t.artists.bookWith} {a.name}
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Artists;
